/**
 * Date formatting utilities
 */

import { DATE_FORMATS } from '../../config/constants';

/**
 * Format date to short format (YY.MM.DD)
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string (YY.MM.DD)
 *
 * @example
 * formatDateShort('2024-03-15') // '24.03.15'
 */
export const formatDateShort = (dateString: string | Date): string => {
  if (!dateString) return '-';

  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

  if (isNaN(date.getTime())) return '-';

  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}.${month}.${day}`;
};

/**
 * Format date to long format (Korean locale)
 * @param dateString - ISO date string or Date object
 * @param includeTime - Whether to include time
 * @returns Formatted date string in Korean
 *
 * @example
 * formatDateLong('2024-03-15T14:30:00') // '2024년 3월 15일'
 * formatDateLong('2024-03-15T14:30:00', true) // '2024년 3월 15일 14:30'
 */
export const formatDateLong = (dateString: string | Date, includeTime = false): string => {
  if (!dateString) return '-';

  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

  if (isNaN(date.getTime())) return '-';

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
  }

  return date.toLocaleDateString(DATE_FORMATS.KOREAN, options);
};

/**
 * Format date with custom format
 * @param dateString - ISO date string or Date object
 * @param format - 'short' or 'long'
 * @returns Formatted date string
 */
export const formatDate = (
  dateString: string | Date,
  format: 'short' | 'long' = 'short'
): string => {
  return format === 'short' ? formatDateShort(dateString) : formatDateLong(dateString);
};

/**
 * Format birth date from various formats (YYYYMMDD or ISO)
 * @param dateString - Birth date string
 * @returns Formatted Korean date string
 *
 * @example
 * formatBirthDate('19900315') // '1990년 3월 15일'
 * formatBirthDate('1990-03-15') // '1990년 3월 15일'
 */
export const formatBirthDate = (dateString: string): string => {
  if (!dateString) return '-';

  let date: Date;

  // Handle YYYYMMDD format
  if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);
    date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  } else {
    // Handle ISO format
    date = new Date(dateString);
  }

  if (isNaN(date.getTime())) return '-';

  return date.toLocaleDateString(DATE_FORMATS.KOREAN, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Format date and time for display
 * @param dateString - ISO date string
 * @returns Formatted date and time
 *
 * @example
 * formatDateTime('2024-03-15T14:30:00') // '2024년 3월 15일 14시 30분'
 */
export const formatDateTime = (dateString: string | Date): string => {
  if (!dateString) return '-';

  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

  if (isNaN(date.getTime())) return '-';

  return date.toLocaleDateString(DATE_FORMATS.KOREAN, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Get relative time from now
 * @param dateString - ISO date string
 * @returns Relative time string (e.g., "2 hours ago", "in 3 days")
 */
export const getRelativeTime = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return '방금 전';
  if (diffMinutes < 60) return `${diffMinutes}분 전`;
  if (diffHours < 24) return `${diffHours}시간 전`;
  if (diffDays < 7) return `${diffDays}일 전`;

  return formatDateShort(date);
};
